// PreventivoWizard — wizard riusabile per richiesta preventivo
// Usato sia dalla pagina /richiesta-preventivo che inline nelle pagine corso
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Check, CheckCircle2, AlertCircle,
    Factory, HardHat, UtensilsCrossed, Briefcase, ShoppingBag, HeartPulse, FileText,
    Users2, Building2, MapPin, GraduationCap, Activity, ShieldCheck, Minus, Plus,
} from "lucide-react";
import { submitQuote } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import { pushLeadToDataLayer } from "@/lib/dataLayer";

const STEPS = [
    { id: "sector", label: "Settore" },
    { id: "participants", label: "Partecipanti" },
    { id: "course", label: "Corso" },
    { id: "location", label: "Location" },
    { id: "summary", label: "Riepilogo" },
];

const SECTORS = [
    { id: "industria", icon: Factory, label: "Industria manifatturiera" },
    { id: "edilizia", icon: HardHat, label: "Edilizia e costruzioni" },
    { id: "ristorazione", icon: UtensilsCrossed, label: "Ristorazione e hotel" },
    { id: "uffici", icon: Briefcase, label: "Uffici e servizi" },
    { id: "retail", icon: ShoppingBag, label: "Retail e commercio" },
    { id: "sanita", icon: HeartPulse, label: "Sanità e assistenza" },
    { id: "altro", icon: FileText, label: "Altro settore" },
];

const MIN_PARTICIPANTS = 1;
const MAX_PARTICIPANTS = 250;
const clampParticipants = (n) => Math.min(MAX_PARTICIPANTS, Math.max(MIN_PARTICIPANTS, Number.isFinite(n) ? Math.round(n) : MIN_PARTICIPANTS));
const participantsLabel = (n) => `${n} ${n === 1 ? "persona" : "persone"}`;

export const COURSE_OPTIONS = [
    { id: "bls-aed-src", title: "BLS-AED SRC", desc: "Obbligo di legge OLL3 · 3-4 ore", icon: ShieldCheck },
    { id: "bls-aed-misv", title: "BLS-AED SRC + MISV", desc: "Corso completo · 6-7 ore", icon: HeartPulse },
    { id: "workshop-aziendali", title: "Workshop Aziendali", desc: "Su misura · da 30 min", icon: Users2 },
    { id: "samaritani", title: "Corso Samaritani", desc: "Per la patente di guida · CHF 140", icon: GraduationCap },
    { id: "defibrillatore", title: "Defibrillatore in noleggio", desc: "AED + manutenzione inclusa", icon: Activity },
    { id: "non-so", title: "Non sono sicuro", desc: "Aiutateci a scegliere il corso giusto", icon: FileText },
];

const LOCATIONS = [
    { id: "vostra-sede", title: "In una sede 7MED", desc: "Mendrisio · Savosa · Bellinzona · Locarno", icon: Building2 },
    { id: "in-azienda", title: "Presso la nostra azienda", desc: "L'istruttore viene da voi", icon: MapPin },
    { id: "ibrida", title: "Decidiamo insieme", desc: "Valutiamo la soluzione migliore", icon: FileText },
];

const SEDI_LIST = ["Mendrisio", "Savosa", "Bellinzona", "Locarno"];

export default function PreventivoWizard({ preselectedCourse = null, idPrefix = "wizard" }) {
    const initialStep = preselectedCourse ? 0 : 0; // start always from 0 for clarity, course is just pre-checked
    const [step, setStep] = useState(initialStep);
    const [data, setData] = useState({
        sector: null,
        participants: MIN_PARTICIPANTS,
        course: preselectedCourse || null,
        location: null,
        sede: null,
        company: "",
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [status, setStatus] = useState({ loading: false, success: null, error: null });

    // If preselectedCourse changes (e.g. route change), reset state
    useEffect(() => {
        if (preselectedCourse) {
            setData((s) => ({ ...s, course: preselectedCourse }));
        }
    }, [preselectedCourse]);

    const setField = (k, v) => setData((s) => ({ ...s, [k]: v }));

    function canProceed() {
        if (step === 0) return !!data.sector;
        if (step === 1) return data.participants >= MIN_PARTICIPANTS && data.participants <= MAX_PARTICIPANTS;
        if (step === 2) return !!data.course;
        if (step === 3) return !!data.location && (data.location !== "vostra-sede" || !!data.sede);
        if (step === 4) return data.company.trim().length >= 2 && data.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(data.email);
        return false;
    }

    function next() { if (canProceed() && step < STEPS.length - 1) setStep((s) => s + 1); }
    function back() { if (step > 0) setStep((s) => s - 1); }

    async function submit() {
        const sector = SECTORS.find((s) => s.id === data.sector);
        const course = COURSE_OPTIONS.find((c) => c.id === data.course);
        const loc = LOCATIONS.find((l) => l.id === data.location);

        const messageParts = [
            `Settore: ${sector?.label || "—"}`,
            `Partecipanti: ${participantsLabel(data.participants)}`,
            `Modalità: ${loc?.title || "—"}${data.sede ? ` (sede ${data.sede})` : ""}`,
            data.message ? `\nNote:\n${data.message}` : "",
        ].filter(Boolean).join("\n");

        setStatus({ loading: true, success: null, error: null });
        try {
            const res = await submitQuote({
                name: data.name.trim(),
                email: data.email.trim(),
                phone: data.phone.trim() || null,
                company: data.company.trim() || null,
                course_interest: course?.title || "Da definire",
                participants: data.participants,
                message: messageParts,
            });
            // GA4/GTM Enhanced Conversions push
            pushLeadToDataLayer({
                form_name: "preventivo_wizard",
                name: data.name,
                email: data.email,
                phone: data.phone,
                company: data.company,
                course: course?.title || "Da definire",
                participants: data.participants,
                message: messageParts,
            });
            setStatus({ loading: false, success: res.message || "Richiesta inviata!", error: null });
        } catch (err) {
            setStatus({ loading: false, success: null, error: formatApiErrorDetail(err.response?.data?.detail) });
        }
    }

    const isComplete = !!status.success;
    const tid = (suffix) => `${idPrefix}-${suffix}`;

    if (isComplete) {
        return (
            <div className="text-center py-12 sm:py-16" data-testid={tid("success")}>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-50 text-emerald-600 mb-8">
                    <CheckCircle2 className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="label-eyebrow text-brand">Richiesta ricevuta</div>
                <h3 className="mt-4 font-heading font-black text-3xl sm:text-4xl tracking-tighter leading-tight">Grazie {data.name.split(" ")[0]}!</h3>
                <p className="mt-6 text-readable max-w-md mx-auto">
                    {status.success} Riceverai una conferma a <strong className="text-ink">{data.email}</strong>.
                </p>
            </div>
        );
    }

    return (
        <>
            {/* Stepper */}
            <div className="mb-8 sm:mb-12" data-testid={tid("stepper")}>
                <div className="grid grid-cols-5 gap-1 sm:gap-2">
                    {STEPS.map((s, i) => {
                        let stepColor = "text-ink/30";
                        if (i === step) stepColor = "text-brand";
                        else if (i < step) stepColor = "text-ink-muted";
                        return (
                            <div key={s.id} className="flex flex-col gap-2">
                                <div className={`h-1 w-full transition-colors ${i <= step ? "bg-brand" : "bg-ink/10"}`} />
                                <div className={`text-[10px] sm:text-xs font-mono uppercase tracking-[0.14em] sm:tracking-[0.18em] text-center sm:text-left ${stepColor}`}>
                                    <span className="sm:hidden">{i + 1}</span>
                                    <span className="hidden sm:inline">{String(i + 1).padStart(2, "0")} · {s.label}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div data-testid={tid("content")}>
                {step === 0 && (
                    <div className="animate-in fade-in duration-500" data-testid={tid("step-sector")}>
                        <StepHeader title="In che settore opera la tua azienda?" subtitle="Questo ci aiuta a personalizzare il corso." />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {SECTORS.map((s) => (
                                <OptionCard key={s.id} icon={s.icon} label={s.label} selected={data.sector === s.id} onClick={() => setField("sector", s.id)} testid={tid(`sector-${s.id}`)} />
                            ))}
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <div className="animate-in fade-in duration-500" data-testid={tid("step-participants")}>
                        <StepHeader title="Quante persone vuoi formare?" subtitle="Un'idea di massima va benissimo, possiamo affinare insieme." />
                        <ParticipantsCounter value={data.participants} onChange={(n) => setField("participants", n)} tid={tid} />
                    </div>
                )}

                {step === 2 && (
                    <div className="animate-in fade-in duration-500" data-testid={tid("step-course")}>
                        <StepHeader title="Quale corso ti interessa?" subtitle="Se non sei sicuro scegli «Non sono sicuro» e ti guidiamo noi." />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            {COURSE_OPTIONS.map((c) => (
                                <DetailedCard key={c.id} icon={c.icon} title={c.title} desc={c.desc} selected={data.course === c.id} onClick={() => setField("course", c.id)} testid={tid(`course-${c.id}`)} />
                            ))}
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="animate-in fade-in duration-500" data-testid={tid("step-location")}>
                        <StepHeader title="Dove vuoi svolgere il corso?" subtitle="Veniamo da te o ti accogliamo in una nostra sede." />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                            {LOCATIONS.map((l) => (
                                <DetailedCard key={l.id} icon={l.icon} title={l.title} desc={l.desc} selected={data.location === l.id} onClick={() => setField("location", l.id)} testid={tid(`location-${l.id}`)} />
                            ))}
                        </div>
                        {data.location === "vostra-sede" && (
                            <div className="mt-8" data-testid={tid("sede-picker")}>
                                <div className="label-eyebrow text-brand">Quale sede preferisci?</div>
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {SEDI_LIST.map((city) => (
                                        <button key={city} onClick={() => setField("sede", city)}
                                            className={`p-4 text-center border-2 transition-all ${data.sede === city ? "bg-brand text-white border-brand" : "bg-white text-ink border-ink/15 hover:border-ink"}`}
                                            data-testid={tid(`sede-${city.toLowerCase()}`)}>
                                            <div className="font-heading font-bold">{city}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 4 && (
                    <div className="animate-in fade-in duration-500" data-testid={tid("step-summary")}>
                        <StepHeader title="Quasi fatto. Lasciaci i tuoi contatti." subtitle="Ti invieremo il preventivo personalizzato entro 24 ore." />
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                            <aside className="lg:col-span-5 bg-[#FAFAFA] border border-ink/10 p-6 lg:p-7 self-start lg:sticky lg:top-24">
                                <div className="label-eyebrow text-brand">Riepilogo</div>
                                <h4 className="mt-2 font-heading font-extrabold text-2xl tracking-tight">Le tue scelte</h4>
                                <dl className="mt-5 space-y-4 text-[15px]">
                                    <Recap label="Settore" value={SECTORS.find((s) => s.id === data.sector)?.label} />
                                    <Recap label="Partecipanti" value={participantsLabel(data.participants)} />
                                    <Recap label="Corso" value={COURSE_OPTIONS.find((c) => c.id === data.course)?.title} />
                                    <Recap label="Location" value={`${LOCATIONS.find((l) => l.id === data.location)?.title || "—"}${data.sede ? ` · ${data.sede}` : ""}`} />
                                </dl>
                            </aside>
                            <div className="lg:col-span-7 space-y-5">
                                <Field label="Azienda" required>
                                    <input value={data.company} onChange={(e) => setField("company", e.target.value)} className="wiz-input" placeholder="Nome dell'azienda" data-testid={tid("company")} />
                                </Field>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Nome e cognome" required>
                                        <input value={data.name} onChange={(e) => setField("name", e.target.value)} className="wiz-input" data-testid={tid("name")} />
                                    </Field>
                                    <Field label="Email" required>
                                        <input type="email" value={data.email} onChange={(e) => setField("email", e.target.value)} className="wiz-input" data-testid={tid("email")} />
                                    </Field>
                                </div>
                                <Field label="Telefono">
                                    <input type="tel" value={data.phone} onChange={(e) => setField("phone", e.target.value)} className="wiz-input" data-testid={tid("phone")} />
                                </Field>
                                <Field label="Note (opzionale)">
                                    <textarea rows={3} value={data.message} onChange={(e) => setField("message", e.target.value)} className="wiz-input resize-none" placeholder="Esigenze particolari, date preferite, dettagli…" data-testid={tid("message")} />
                                </Field>
                                {status.error && (
                                    <div className="flex gap-3 items-start p-4 bg-red-50 border border-red-300 text-red-900" data-testid={tid("error")}>
                                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm">{status.error}</div>
                                    </div>
                                )}
                                <p className="text-xs text-ink-muted">
                                    Inviando questo form accetti la nostra <Link to="/privacy-policy" className="underline hover:text-brand">Privacy Policy</Link>.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-10 sm:mt-14 flex flex-col-reverse sm:flex-row gap-4 sm:items-center sm:justify-between">
                    <button onClick={back} disabled={step === 0}
                        className="inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider text-ink hover:text-brand transition-colors disabled:opacity-30 disabled:cursor-not-allowed py-3"
                        data-testid={tid("back")}>
                        <ArrowLeft className="w-4 h-4" /> Indietro
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button onClick={next} disabled={!canProceed()} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid={tid("next")}>
                            Avanti <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button onClick={submit} disabled={!canProceed() || status.loading} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid={tid("submit")}>
                            {status.loading ? "Invio in corso…" : "Invia richiesta"} <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                .wiz-input {
                    width: 100%; background: transparent; border: 0;
                    border-bottom: 1.5px solid rgba(10,10,10,0.2);
                    padding: 12px 0; font-size: 16px; color: #0a0a0a;
                    outline: none; transition: border-color 0.2s;
                    font-family: "IBM Plex Sans", sans-serif;
                }
                .wiz-input:focus { border-bottom-color: #FF3B30; }
                .wiz-input::placeholder { color: rgba(10,10,10,0.35); }
            `}</style>
        </>
    );
}

function ParticipantsCounter({ value, onChange, tid }) {
    const btn = "w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center border-2 border-ink text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ink";
    return (
        <div className="flex flex-col items-start gap-4" data-testid={tid("participants-counter")}>
            <div className="flex items-stretch">
                <button type="button" onClick={() => onChange(clampParticipants(value - 1))} disabled={value <= MIN_PARTICIPANTS}
                    className={btn} aria-label="Diminuisci partecipanti" data-testid={tid("participants-minus")}>
                    <Minus className="w-6 h-6" strokeWidth={2.5} />
                </button>
                <input type="number" inputMode="numeric" min={MIN_PARTICIPANTS} max={MAX_PARTICIPANTS} value={value}
                    onChange={(e) => onChange(clampParticipants(parseInt(e.target.value, 10)))}
                    className="w-28 sm:w-36 h-16 sm:h-20 text-center font-heading font-black text-3xl sm:text-4xl tracking-tighter border-y-2 border-ink bg-white outline-none focus:bg-[#FAFAFA] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    aria-label="Numero di partecipanti" data-testid={tid("participants-value")} />
                <button type="button" onClick={() => onChange(clampParticipants(value + 1))} disabled={value >= MAX_PARTICIPANTS}
                    className={btn} aria-label="Aumenta partecipanti" data-testid={tid("participants-plus")}>
                    <Plus className="w-6 h-6" strokeWidth={2.5} />
                </button>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted" data-testid={tid("participants-label")}>
                {participantsLabel(value)} · max {MAX_PARTICIPANTS}
            </div>
        </div>
    );
}

function StepHeader({ title, subtitle }) {
    return (
        <div className="mb-8 sm:mb-10">
            <h3 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter leading-[1.1]">{title}</h3>
            <p className="mt-3 text-base sm:text-lg text-ink-muted max-w-xl">{subtitle}</p>
        </div>
    );
}

function OptionCard({ icon: Icon, label, selected, onClick, testid }) {
    return (
        <button onClick={onClick}
            className={`flex items-center gap-4 p-5 sm:p-6 text-left border-2 transition-all ${selected ? "bg-ink text-white border-ink" : "bg-white text-ink border-ink/15 hover:border-ink"}`}
            data-testid={testid}>
            <Icon className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 text-brand" strokeWidth={1.5} />
            <span className="font-heading font-semibold text-base sm:text-lg leading-tight flex-1">{label}</span>
            {selected && <Check className="w-5 h-5 text-brand flex-shrink-0" strokeWidth={2.5} />}
        </button>
    );
}

function DetailedCard({ icon: Icon, title, desc, selected, onClick, testid }) {
    return (
        <button onClick={onClick}
            className={`p-5 sm:p-6 text-left border-2 transition-all ${selected ? "bg-ink text-white border-ink" : "bg-white text-ink border-ink/15 hover:border-ink"}`}
            data-testid={testid}>
            <div className="flex items-start gap-4">
                <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-brand flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div className="flex-1">
                    <div className="font-heading font-bold text-lg sm:text-xl leading-tight">{title}</div>
                    <div className={`mt-1.5 text-sm leading-relaxed ${selected ? "text-white/75" : "text-ink-muted"}`}>{desc}</div>
                </div>
                {selected && <Check className="w-5 h-5 text-brand flex-shrink-0 mt-1" strokeWidth={2.5} />}
            </div>
        </button>
    );
}

function Recap({ label, value }) {
    return (
        <div className="border-b border-ink/10 pb-3">
            <dt className="text-[10px] font-mono uppercase tracking-[0.18em] text-ink-muted">{label}</dt>
            <dd className="mt-1 font-heading font-bold text-base text-ink">{value || "—"}</dd>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] font-mono font-bold text-ink/70">
                {label}{required && <span className="text-brand"> *</span>}
            </span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
